import {
  Typography,
  Accordion,
  AccordionSummary,
  IconButton,
  Box,
  AccordionDetails,
  Chip,
  Collapse,
} from "@mui/material";
import { ChevronDown, ChevronRight, Circle, RefreshCw } from "lucide-react";
import { CertBanner } from "./CertBanner";
import { useEffect, useState } from "react";

interface SWInfo {
  version: string;
  status: string;
  scope: string;
  quota: string;
  usage: string;
  online: boolean;
  secure: boolean;
  standalone: boolean;
  origin: string;
  path: string;
  viewport: string;
  lang: string;
  ua: string;
  caches: { name: string; urls: string[] }[];
}

interface CacheEntry {
  name: string;
  urls: string[];
}

export async function getSWInfo(): Promise<SWInfo> {
  const nav = navigator;
  const dpr = window.devicePixelRatio ?? 1;
  const base: Omit<SWInfo, "version" | "status" | "scope" | "quota" | "usage" | "caches"> = {
    online: nav.onLine,
    secure: window.location.protocol === "https:",
    standalone: window.matchMedia("(display-mode: standalone)").matches,
    origin: window.location.origin,
    path: window.location.pathname || "/",
    viewport: `${window.innerWidth}×${window.innerHeight} (dpr ${Math.round(dpr * 10) / 10})`,
    lang: nav.language ?? "—",
    ua: nav.userAgent,
  };

  let version = "(controller 없음)";
  let status = "unregistered";
  let scope = "—";
  let quota = "—";
  let usage = "0 B";
  let caches: CacheEntry[] = [];

  if ("serviceWorker" in nav) {
    try {
      const reg = await nav.serviceWorker.getRegistration();
      if (reg) {
        const controller = nav.serviceWorker.controller; // ← 여기
        const sw = controller ?? reg.active ?? reg.installing ?? reg.waiting;
        version = controller ? "(controller 있음)" : "(controller 없음)";
        status = sw?.state ?? "registered";
        scope = reg.scope;
      }
    } catch {}
  }

  if ("storage" in nav && "estimate" in nav.storage) {
    try {
      const est = await nav.storage.estimate();
      const toMB = (b: number) => (b / 1024 / 1024).toFixed(1) + " MB";
      if (est.quota) quota = toMB(est.quota);
      if (est.usage != null) usage = est.usage === 0 ? "0 B" : toMB(est.usage);
    } catch {}
  }

  if ("caches" in window) {
    try {
      const cacheNames = await window.caches.keys();
      caches = await Promise.all(
        cacheNames.map(async (name) => {
          const cache = await window.caches.open(name);
          const keys = await cache.keys();
          return {
            name,
            urls: keys.map((r) => new URL(r.url).pathname),
          };
        }),
      );
    } catch {}
  }

  return { ...base, version, status, scope, quota, usage, caches };
}

export default function SWStatusSection() {
  const [swInfo, setSWInfo] = useState<SWInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const swTitle = swInfo
    ? `SW · ${swInfo.version.includes("있음") ? "controller" : "no-controller"} · ${swInfo.status} · ${swInfo.online ? "online" : "offline"}`
    : "SW · 로딩 중...";

  const loadSW = async () => {
    setLoading(true);
    const info = await getSWInfo();
    setSWInfo(info);
    setLoading(false);
  };

  useEffect(() => {
    loadSW();
  }, []);

  return (
    <>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.07em",
          color: "text.secondary",
          textTransform: "uppercase",
          mb: 1.5,
        }}
      >
        서비스 워커 상태
      </Typography>
      {swInfo && swInfo.status !== "activated" && <CertBanner />}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "action.hover",
        }}
      >
        <Accordion
          disableGutters
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, "&:before": { display: "none" } }}
        >
          <AccordionSummary
            expandIcon={<ChevronDown size={16} />}
            sx={{ px: 2, minHeight: 44, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 } }}
          >
            <Circle
              size={10}
              fill={swInfo?.online ? "currentColor" : "none"}
              style={{ color: swInfo?.online ? "#4caf50" : "#9e9e9e" }}
            />
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{swTitle}</Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                loadSW();
              }}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              <RefreshCw size={14} />
            </IconButton>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ maxHeight: 360, overflowY: "auto", borderTop: "1px solid", borderColor: "divider" }}>
              {swInfo && (
                <>
                  <InfoSection label="SW">
                    <InfoRow label="version">{swInfo.version}</InfoRow>
                    <InfoRow label="status">
                      <Chip
                        label={swInfo.status}
                        size="small"
                        color={swInfo.status === "activated" ? "success" : "default"}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </InfoRow>
                    <InfoRow label="scope">{swInfo.scope}</InfoRow>
                  </InfoSection>

                  <InfoSection label="Storage">
                    <InfoRow label="quota">{swInfo.quota}</InfoRow>
                    <InfoRow label="usage">{swInfo.usage}</InfoRow>
                  </InfoSection>

                  <InfoSection label="ENV">
                    <InfoRow label="online">
                      <BoolChip value={swInfo.online} />
                    </InfoRow>
                    <InfoRow label="secure">
                      <BoolChip value={swInfo.secure} />
                    </InfoRow>
                    <InfoRow label="standalone">
                      <BoolChip value={swInfo.standalone} />
                    </InfoRow>
                    <InfoRow label="origin">{swInfo.origin}</InfoRow>
                    <InfoRow label="path">{swInfo.path}</InfoRow>
                    <InfoRow label="viewport">{swInfo.viewport}</InfoRow>
                    <InfoRow label="lang">{swInfo.lang}</InfoRow>
                    <InfoRow label="ua">{swInfo.ua}</InfoRow>
                  </InfoSection>
                  {swInfo.caches.length > 0 && (
                    <InfoSection label="Caches">
                      {swInfo.caches.map((c) => (
                        <CacheEntry key={c.name} cache={c} />
                      ))}
                    </InfoSection>
                  )}
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  );
}

function BoolChip({ value }: { value: boolean }) {
  return (
    <Chip
      label={String(value)}
      size="small"
      color={value ? "success" : "default"}
      sx={{ height: 20, fontSize: 11, fontFamily: "monospace" }}
    />
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 2, py: 0.3, alignItems: "baseline" }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 100, flexShrink: 0 }}>{label}</Typography>
      <Typography
        component="span"
        sx={{ fontSize: 12, fontFamily: "monospace", wordBreak: "break-all", color: "text.primary" }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function InfoSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ px: 2, py: 1.5, "& + &": { borderTop: "1px solid", borderColor: "divider" } }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.07em",
          color: "info.main",
          textTransform: "uppercase",
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function CacheEntry({ cache }: { cache: { name: string; urls: string[] } }) {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          cursor: "pointer",
          py: 0.5,
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <ChevronRight
          size={13}
          style={{
            transition: "transform 0.18s",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            color: "var(--color-text-secondary)",
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.primary" }}>{cache.name}</Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}>{cache.urls.length} entries</Typography>
      </Box>

      <Collapse in={open}>
        <Box sx={{ pl: 2.5, pb: 0.5 }}>
          {cache.urls.map((url) => (
            <Typography
              key={url}
              sx={{
                fontSize: 11,
                fontFamily: "monospace",
                color: "text.secondary",
                lineHeight: 1.9,
              }}
            >
              {url}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
